import { useEffect, useMemo, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import ExposurePlus1Icon from "@mui/icons-material/PlusOne";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { fetchTeChronoRecords } from "../api/coachChronoApi";
import { fetchTeCountRecords } from "../api/coachCountApi";
import { formatChronoClock } from "../utils/coachChronoPlayers";
import { peekSignedMediaUrl, resolveSignedMediaUrl } from "../api/signedMediaUrl";
import { ReviewVideoFullscreenPlayer } from "./ReviewVideoFullscreenPlayer";

function pickUserRecord(data, userId) {
  if (!userId) return null;
  return (data?.records || []).find((r) => String(r.user_id) === String(userId)) || null;
}

/**
 * Chronometer / counter results recorded by coach tools for one player on a
 * training exercise. Independent of the catalogue exercise details.
 */
export function ExerciseToolResults({ teId, userId, showEmpty = false, refreshKey = 0, t }) {
  const [loading, setLoading] = useState(false);
  const [chrono, setChrono] = useState(null);
  const [count, setCount] = useState(null);
  const [playUrl, setPlayUrl] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    if (!teId || !userId) {
      setChrono(null);
      setCount(null);
      return undefined;
    }
    const ac = new AbortController();
    setLoading(true);
    Promise.all([
      fetchTeChronoRecords(teId, { signal: ac.signal }),
      fetchTeCountRecords(teId, { signal: ac.signal }),
    ])
      .then(([chronoData, countData]) => {
        setChrono(pickUserRecord(chronoData, userId));
        setCount(pickUserRecord(countData, userId));
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setChrono(null);
        setCount(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [teId, userId, refreshKey]);

  const video = chrono?.video?.url ? chrono.video : null;

  useEffect(() => {
    if (!videoOpen || !video?.url) {
      setPlayUrl(null);
      return undefined;
    }
    let cancelled = false;
    const cached = peekSignedMediaUrl(video.url);
    setPlayUrl(cached || video.url);
    if (cached) return undefined;
    resolveSignedMediaUrl(video.url)
      .then((signed) => {
        if (!cancelled && signed) setPlayUrl(signed);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [videoOpen, video?.url]);

  const clip = useMemo(() => {
    if (!video) return { start: 0, end: null };
    const start = video.start_ms != null ? Math.max(0, Number(video.start_ms) / 1000) : 0;
    const end = video.end_ms != null ? Math.max(start, Number(video.end_ms) / 1000) : null;
    return { start, end };
  }, [video]);

  const chronoValue = chrono?.metric_value;
  const countValue = count?.metric_value;
  const hasChrono = chronoValue != null && Number.isFinite(Number(chronoValue));
  const hasCount = countValue != null && Number.isFinite(Number(countValue));

  if (loading && !hasChrono && !hasCount) {
    return showEmpty ? (
      <div className="player-session-results__loading">
        <CircularProgress size={28} />
      </div>
    ) : null;
  }

  if (!hasChrono && !hasCount) {
    return showEmpty ? (
      <div className="player-session-results__empty">
        <p>
          {t(
            "perfNoToolResults",
            "No chrono or counter result recorded for this player on this exercise."
          )}
        </p>
      </div>
    ) : null;
  }

  return (
    <section className="exercise-tool-results">
      <h3 className="player-exercise-details__perf-list-heading">
        {t("perfToolResultsTitle", "Recorded results")}
      </h3>
      <div className="exercise-tool-results__grid">
        {hasChrono ? (
          <div className="exercise-tool-results__card">
            <TimerOutlinedIcon className="exercise-tool-results__icon" />
            <div className="exercise-tool-results__text">
              <span className="exercise-tool-results__label">
                {t("perfChronoTime", "Chrono time")}
              </span>
              <span className="exercise-tool-results__value">
                {formatChronoClock(Number(chronoValue))}
              </span>
            </div>
          </div>
        ) : null}
        {hasCount ? (
          <div className="exercise-tool-results__card">
            <ExposurePlus1Icon className="exercise-tool-results__icon" />
            <div className="exercise-tool-results__text">
              <span className="exercise-tool-results__label">
                {count.count_label || t("perfCounter", "Count")}
              </span>
              <span className="exercise-tool-results__value">{Number(countValue)}</span>
            </div>
          </div>
        ) : null}
      </div>
      {video ? (
        <button
          type="button"
          className="exercise-tool-results__video-btn"
          onClick={() => setVideoOpen(true)}
        >
          <PlayCircleFilledIcon fontSize="small" />
          <span>{t("perfWatchVideo", "Watch video")}</span>
        </button>
      ) : null}
      {videoOpen && playUrl ? (
        <ReviewVideoFullscreenPlayer
          url={playUrl}
          onClose={() => setVideoOpen(false)}
          title={t("perfChronoTime", "Chrono time")}
          autoPlay
          enablePose={false}
          isEvaluationVideo={false}
          messageId={video.message_id || null}
          teId={teId}
          initialCurrentTime={clip.start}
          playbackWindowStart={clip.start}
          playbackWindowEnd={clip.end}
        />
      ) : null}
    </section>
  );
}

export default ExerciseToolResults;
